import { motion } from 'framer-motion';
import { Camera, Car, Save, Upload, User } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { Button, Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input.js';
import './UserOnboarding.css';
import { checkPayloadSize, getAllRoles } from './onboardingApi.js';

const UserOnboarding = () => {
  // Log screen view
  useScreenLogging('UserOnboarding', {
    component: 'UserOnboarding',
    onboardingType: 'individual',
  });
  const [formData, setFormData] = useState({
    'Employee ID': '',
    'Full Name': '',
    'Date of Birth': '',
    'Phone Number': '',
    // Email: '',
    Gender: '',
    'Blood Group': '',
    'Area Available': '',
    Address: '',
    'Reports To': '',
    'Commercial License': false,
    'International Driving Permit': false,
    '4 Wheeler': false,
    'Heavy Motor': false,
    role_name: 'Driver',
  });

  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hasImage, setHasImage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imageSuccess, setImageSuccess] = useState('');
  const [roles, setRoles] = useState([]);

  const [isDriver, setIsDriver] = useState(false);

  const [isLoadingData, setIsLoadingData] = useState(true);
  const fileInputRef = useRef(null);

  // Camera functionality state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);

  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraContainerRef = useRef(null);

  const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
  const genders = ['Male', 'Female', 'Other'];

  // Fetch roles and managers on component mount
  useEffect(() => {
    const fetchRolesAndManagers = async () => {
      setIsLoadingData(true);
      try {
        console.log('Fetching roles and managers from API...');

        // Fetch roles from API
        const rolesResponse = await getAllRoles();
        console.log('Roles response:', rolesResponse);
        
        // Log API event for fetching roles
        try {
          await loggingService.logInfo(
            'user_onboarding_fetch_roles',
            'Fetched roles for user onboarding',
            {
              endpoint: '/role/getall',
              method: 'GET',
              rolesCount: Array.isArray(rolesResponse) ? rolesResponse.length : (rolesResponse?.roles?.length || 0),
            }
          );
        } catch (logError) {
          console.warn('[UserOnboarding] Failed to log event:', logError);
        }

        // Ensure roles is always an array
        if (Array.isArray(rolesResponse)) {
          setRoles(rolesResponse);
        } else if (rolesResponse && Array.isArray(rolesResponse.roles)) {
          setRoles(rolesResponse.roles);
        } else {
          console.warn('Unexpected roles response format:', rolesResponse);
          // Fallback to mock data if API returns unexpected format
          const fallbackRoles = [
            {
              role_name: 'driver',
              display_name: 'Driver',
              base_role: 'driver',
            },
            {
              role_name: 'security_officer',
              display_name: 'Security Officer',
              base_role: 'security',
            },
            {
              role_name: 'supervisor',
              display_name: 'Supervisor',
              base_role: 'supervisor',
            },
            {
              role_name: 'manager',
              display_name: 'Manager',
              base_role: 'management',
            },
            { role_name: 'admin', display_name: 'Admin', base_role: 'admin' },
          ];
          setRoles(fallbackRoles);
        }

        setError(''); // Clear any previous errors
      } catch (error) {
        console.error('Error fetching roles and managers:', error);

        // More specific error message
        if (error.message.includes('Failed to fetch')) {
          setError(
            'Development Mode: Using fallback data. API calls blocked by CORS in development.'
          );
        } else if (
          error.message.includes('401') ||
          error.message.includes('403')
        ) {
          setError('Authentication error: Please check your credentials.');
        } else if (error.message.includes('CORS')) {
          setError(
            'Development Mode: CORS policy blocking API calls. Using fallback data.'
          );
        } else {
          setError(`API Error: ${error.message}. Using fallback data.`);
        }

        // Fallback to mock data if API fails
        const fallbackRoles = [
          { role_name: 'driver', display_name: 'Driver', base_role: 'driver' },
          {
            role_name: 'security_officer',
            display_name: 'Security Officer',
            base_role: 'security',
          },
          {
            role_name: 'supervisor',
            display_name: 'Supervisor',
            base_role: 'supervisor',
          },
          {
            role_name: 'manager',
            display_name: 'Manager',
            base_role: 'management',
          },
          { role_name: 'admin', display_name: 'Admin', base_role: 'admin' },
        ];
        setRoles(fallbackRoles);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchRolesAndManagers();
  }, []);

  // Cleanup camera on component unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Check camera availability on mount
  useEffect(() => {
    const checkCamera = async () => {
      const cameraCheck = await checkCameraAvailability();
      if (!cameraCheck.available) {
        setCameraError(cameraCheck.error);
      }
    };

    checkCamera();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Enhanced image upload with smart compression and API Gateway optimization
  const handleImageUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size before processing (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError(
        `Image file is too large (${(file.size / (1024 * 1024)).toFixed(
          2
        )} MB). Maximum allowed size is 5MB.`
      );
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setImageSuccess('Analyzing image...');

      const reader = new FileReader();
      reader.onload = async e => {
        try {
          const base64 = e.target.result.split(',')[1];
          const originalSizeKB = base64.length / 1024;

          // Import enhanced compression functions
          const {
            assessImageQuality,
            preprocessImageForCompression,
            progressiveCompressImage,
          } = await import('./onboardingApi.js');

          // Assess image quality and determine optimal compression strategy
          setImageSuccess('Assessing image quality...');
          const qualityInfo = await assessImageQuality(base64);
          console.log('📊 Image quality assessment:', qualityInfo);

          // Preprocess for optimal compression settings
          setImageSuccess('Optimizing compression settings...');
          const preprocessInfo = await preprocessImageForCompression(base64);
          console.log('🎯 Optimal compression settings:', preprocessInfo);

          // Use progressive compression for best results
          setImageSuccess('Compressing image...');
          const compressedImage = await progressiveCompressImage(base64, {
            maxWidth: preprocessInfo.targetWidth,
            maxHeight: preprocessInfo.targetHeight,
            targetSizeKB: preprocessInfo.targetSizeKB,
            onProgress: (message, progress) => {
              setImageSuccess(`${message} (${Math.round(progress * 100)}%)`);
            },
          });

          const finalSizeKB = compressedImage.length / 1024;
          const reductionPercent = (
            ((originalSizeKB - finalSizeKB) / originalSizeKB) *
            100
          ).toFixed(1);

          // Set the optimized image
          setImageData(compressedImage);
          setImagePreview(e.target.result);
          setHasImage(true);

          setImageSuccess(
            `✅ Image optimized successfully! ${originalSizeKB.toFixed(
              1
            )} KB → ${finalSizeKB.toFixed(
              1
            )} KB (${reductionPercent}% reduction)`
          );

          console.log(
            `🚀 Image optimization complete: ${qualityInfo.width}x${qualityInfo.height} → ${preprocessInfo.targetWidth}x${preprocessInfo.targetHeight}`
          );
          console.log(
            `📊 Compression: ${originalSizeKB.toFixed(
              1
            )} KB → ${finalSizeKB.toFixed(
              1
            )} KB (${reductionPercent}% reduction)`
          );
        } catch (compressionError) {
          console.error('Compression failed:', compressionError);
          setError(`Compression failed: ${compressionError.message}`);
          setImageSuccess('');
          setImageData(null);
          setImagePreview(null);
          setHasImage(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read image file. Please try again.');
        setImageSuccess('');
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setError('Failed to process image. Please try again.');
      setImageSuccess('');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if camera is available
  const checkCameraAvailability = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return {
          available: false,
          error: 'Camera not supported in this browser',
        };
      }

      // Check if any video input devices exist
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        device => device.kind === 'videoinput'
      );

      if (videoDevices.length === 0) {
        return { available: false, error: 'No camera found on this device' };
      }

      return { available: true, devices: videoDevices };
    } catch (err) {
      console.error('Error checking camera availability:', err);
      return { available: false, error: 'Unable to check camera availability' };
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      setCameraError('');
      setIsCameraActive(true);
      setShowCameraPreview(true);

      // First check camera availability
      const cameraCheck = await checkCameraAvailability();
      if (!cameraCheck.available) {
        throw new Error(cameraCheck.error);
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user', // Use front camera by default
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera access error:', err);

      // More specific error messages
      let errorMessage = 'Unable to access camera. Please check permissions.';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera does not meet the required specifications.';
      } else if (err.message.includes('not supported')) {
        errorMessage = 'Camera not supported in this browser.';
      } else if (err.message.includes('No camera found')) {
        errorMessage = 'No camera found on this device.';
      }

      setCameraError(errorMessage);
      setIsCameraActive(false);
      setShowCameraPreview(false);
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setShowCameraPreview(false);
    setCameraError('');
  };

  // Enhanced camera capture with smart compression
  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    try {
      setImageSuccess('Capturing photo...');

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas with high quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(video, 0, 0);

      // Convert to base64 (start with high quality)
      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      const base64 = photoDataUrl.split(',')[1];
      const originalSizeKB = base64.length / 1024;

      // Import enhanced compression functions
      const {
        assessImageQuality,
        preprocessImageForCompression,
        progressiveCompressImage,
      } = await import('./onboardingApi.js');

      // Assess captured image quality
      setImageSuccess('Optimizing captured image...');
      const qualityInfo = await assessImageQuality(base64);
      console.log('📊 Captured image quality:', qualityInfo);

      // Use progressive compression for optimal results
      const compressedImage = await progressiveCompressImage(base64, {
        maxWidth: 800,
        maxHeight: 600,
        targetSizeKB: 300,
        onProgress: (message, progress) => {
          setImageSuccess(
            `Optimizing: ${message} (${Math.round(progress * 100)}%)`
          );
        },
      });

      const finalSizeKB = compressedImage.length / 1024;
      const reductionPercent = (
        ((originalSizeKB - finalSizeKB) / originalSizeKB) *
        100
      ).toFixed(1);

      // Set the optimized captured image
      setImageData(compressedImage);
      setImagePreview(photoDataUrl);
      setHasImage(true);

      // Stop camera after taking photo
      stopCamera();

      setImageSuccess(
        `✅ Photo captured and optimized! ${originalSizeKB.toFixed(
          1
        )} KB → ${finalSizeKB.toFixed(1)} KB (${reductionPercent}% reduction)`
      );

      console.log(
        `📸 Camera capture optimized: ${originalSizeKB.toFixed(
          1
        )} KB → ${finalSizeKB.toFixed(1)} KB (${reductionPercent}% reduction)`
      );
    } catch (err) {
      console.error('Error capturing/optimizing photo:', err);
      setImageSuccess('');
      if (err.message.includes('too large')) {
        setError(err.message);
      } else {
        setError('Failed to capture photo. Please try again.');
      }
    } finally {
      setIsCapturing(false);
    }
  };

  // Switch camera (front/back)
  const switchCamera = async () => {
    if (cameraStream) {
      stopCamera();
      // Wait a bit before starting new camera
      setTimeout(() => {
        startCamera();
      }, 100);
    }
  };

  // Handle camera capture button click
  const handleCameraCapture = () => {
    if (isCameraActive) {
      takePhoto();
    } else {
      startCamera();
    }
  };

  const validateForm = () => {
    if (!formData['Employee ID']) {
      setError('Employee ID is required');
      return false;
    }
    if (!formData['Full Name']) {
      setError('Full Name is required');
      return false;
    }
    if (!formData['Phone Number']) {
      setError('Phone Number is required');
      return false;
    }
    if (!formData['Date of Birth']) {
      setError('Date of Birth is required');
      return false;
    }
    if (!formData['Gender'] || formData['Gender'] === '') {
      setError('Gender is required. Please select a gender.');
      return false;
    }
    if (!formData['Blood Group'] || formData['Blood Group'] === '') {
      setError('Blood Group is required. Please select a blood group.');
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setImageSuccess('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      // Find the selected role to get its base_role
      const selectedRole = roles.find(
        role => role.role_name === formData.role_name
      );
      const baseRole = selectedRole ? selectedRole.base_role : 'General';

      // Log the role mapping for verification
      console.log('🔍 ** ROLE MAPPING VERIFICATION ** 🔍');
      console.log('   • Selected Role Name:', formData.role_name);
      console.log('   • Selected Role Object:', selectedRole);
      console.log('   • Base Role (Designation):', baseRole);
      console.log(
        '   • This base_role will be sent as "Designation" to backend'
      );
      console.log('🎯 ** END ROLE MAPPING ** 🎯');

      // Log whether spec_data will be included
      console.log('📋 ** SPEC_DATA INCLUSION LOGIC ** 📋');
      if (formData.role_name === 'Driver') {
        console.log(
          '   • Driver role selected - spec_data WILL be included with vehicle license info'
        );
      } else {
        console.log(
          '   • Non-driver role selected - spec_data will NOT be included'
        );
      }
      console.log('🎯 ** END SPEC_DATA LOGIC ** 🎯');

      // Format date from YYYY-MM-DD or DD-MM-YYYY to DD/MM/YYYY for lambda compatibility
      const formatDateForLambda = dateString => {
        if (!dateString) return '';

        // Handle YYYY-MM-DD format (convert to DD/MM/YYYY)
        if (dateString.includes('-') && dateString.split('-').length === 3) {
          const parts = dateString.split('-');

          // Check if it's YYYY-MM-DD format
          if (parts[0].length === 4) {
            const [year, month, day] = parts;
            return `${day}/${month}/${year}`;
          }
          // Check if it's DD-MM-YYYY format
          else if (parts[2].length === 4) {
            const [day, month, year] = parts;
            return `${day}/${month}/${year}`;
          }
        }

        // If already in DD/MM/YYYY format, return as is
        if (dateString.includes('/')) {
          return dateString;
        }

        // Return original if format is unrecognized
        return dateString;
      };

      // Prepare the payload based on the Flutter app's formData structure
      const payload = {
        imageData: imageData,
        Designation: baseRole, // Use base_role from API response
        spec_role: formData.role_name, // Lambda expects 'role_name' not 'spec_role'
        spec_data:
          formData.role_name === 'Driver'
            ? {
                // Include driver-specific fields only when role is 'Driver'
                'Commercial License': formData['Commercial License'],
                'International Driving Permit':
                  formData['International Driving Permit'],
                '4 Wheeler': formData['4 Wheeler'],
                'Heavy Motor': formData['Heavy Motor'],
              }
            : undefined, // Only include spec_data if driver role
        'Employee ID': formData['Employee ID'],
        'Full Name': formData['Full Name'],
        Gender: formData['Gender'],
        'Date of Birth': formatDateForLambda(formData['Date of Birth']),
        'Blood Group': formData['Blood Group'],
        Address: formData['Address'],
        'Reports To': formData['Reports To'],
        'Phone Number': formData['Phone Number'],
        'Area Available': formData['Area Available'], // Separate field, not in spec_data
        Email: `${formData['Employee ID']}@innoworx.ai`, // Lambda expects Email field
      };

      // Strict payload size validation
      const sizeCheck = checkPayloadSize(payload);

      if (sizeCheck.action === 'BLOCK_SUBMISSION') {
        setError(
          `❌ Submission blocked: ${
            sizeCheck.recommendation
          } (Current size: ${sizeCheck.size.toFixed(2)} MB)`
        );
        setIsLoading(false);
        return;
      } else if (sizeCheck.action === 'WARN') {
        console.warn(
          `⚠️ ${
            sizeCheck.recommendation
          } (Current size: ${sizeCheck.size.toFixed(2)} MB)`
        );
        setSuccess(`⚠️ Warning: ${sizeCheck.recommendation}`);
      }

      // Call the actual API to create the user
      console.log('🚀 ** CALLING ONBOARDING API ** 🚀');
      console.log('📋 Full Payload:', JSON.stringify(payload, null, 2));
      console.log('📊 Form Data Summary:');
      console.log('   • Employee ID:', payload['Employee ID']);
      console.log('   • Full Name:', payload['Full Name']);
      console.log('   • Date of Birth (original):', formData['Date of Birth']);
      console.log(
        '   • Date of Birth (lambda format):',
        payload['Date of Birth']
      );
      console.log('   • Phone Number:', payload['Phone Number']);
      console.log('   • Gender:', payload['Gender']);
      console.log('   • Blood Group:', payload['Blood Group']);
      console.log('   • Area Available:', payload['Area Available']);
      console.log('   • Address:', payload['Address']);
      console.log('   • Reports To:', payload['Reports To']);
      console.log('   • Designation (base_role):', payload.Designation);
      console.log('   • Role Name:', payload.role_name);
      console.log(
        '   • Spec Data:',
        payload.spec_data || 'Not applicable (role-specific data not needed)'
      );
      if (formData.role_name === 'Driver' && payload.spec_data) {
        console.log(
          '   • Commercial License:',
          payload.spec_data['Commercial License']
        );
        console.log(
          '   • International Driving Permit:',
          payload.spec_data['International Driving Permit']
        );
        console.log('   • 4 Wheeler:', payload.spec_data['4 Wheeler']);
        console.log('   • Heavy Motor:', payload.spec_data['Heavy Motor']);
      }
      console.log('   • Has Image:', payload.imageData ? 'Yes' : 'No');
      console.log(
        '   • Image Data Length:',
        payload.imageData ? `${payload.imageData.length} characters` : 'N/A'
      );
      console.log('   • Tenant ID: NOT SENT TO BACKEND (removed for security)');
      console.log('🎯 ** END OF ONBOARDING DATA ** 🎯');

      try {
        // Import the API functions
        const { createIndividualUser, createDriverUser } = await import('./onboardingApi.js');

        // Check if driver role (case-insensitive)
        const isDriverRole =
          formData.role_name?.toLowerCase() === 'driver' ||
          selectedRole?.display_name?.toLowerCase() === 'driver' ||
          selectedRole?.role_name?.toLowerCase() === 'driver';

        if (isDriverRole) {
          // Convert boolean fields (formData already has booleans, but ensure they're proper booleans)
          const convertToBoolean = value => {
            if (typeof value === 'boolean') return value;
            const val = value?.toString().trim().toLowerCase() || 'no';
            return val === 'yes' || val === 'true' || val === '1';
          };

          // Driver-specific payload for /driver/user/create endpoint
          const driverPayload = {
            imageData: imageData,
            'Employee ID': formData['Employee ID'],
            'Full Name': formData['Full Name'],
            'Phone Number': formData['Phone Number'],
            'Date of Birth': formatDateForLambda(formData['Date of Birth']),
            Gender: formData['Gender'],
            'Blood Group': formData['Blood Group'],
            Address: formData['Address'],
            'Reports to': formData['Reports To'], // Note: driver API uses 'Reports to' (with space)
            'Area Available': formData['Area Available'],
            Email: `${formData['Employee ID']}@innoworx.ai`,
            'Commercial License': convertToBoolean(formData['Commercial License']),
            'International Driving Permit': convertToBoolean(
              formData['International Driving Permit']
            ),
            '4 Wheeler': convertToBoolean(formData['4 Wheeler']),
            'Heavy Motor': convertToBoolean(formData['Heavy Motor']),
          };

          console.log('🚗 Using driver API for onboarding...');
          console.log('📋 Driver Payload:', JSON.stringify(driverPayload, null, 2));
          const response = await createDriverUser(driverPayload);
          console.log('✅ Driver API Response:', response);
          
          // Log API event for creating driver user
          try {
            await loggingService.logInfo(
              'user_onboarding_create_driver',
              'Created driver user',
              {
                endpoint: '/driver/user/create',
                method: 'POST',
                employeeId: driverPayload['Employee ID'],
                hasImage: !!driverPayload.imageData,
                commercialLicense: driverPayload['Commercial License'],
                internationalPermit: driverPayload['International Driving Permit'],
                fourWheeler: driverPayload['4 Wheeler'],
                heavyMotor: driverPayload['Heavy Motor'],
              }
            );
          } catch (logError) {
            console.warn('[UserOnboarding] Failed to log event:', logError);
          }
          
          setSuccess(
            'Driver created successfully! Check browser console for API response details.'
          );
        } else {
          console.log('📡 Calling createIndividualUser API...');
          const response = await createIndividualUser(payload);
          console.log('✅ API Response:', response);
          
          // Log API event for creating individual user
          try {
            await loggingService.logInfo(
              'user_onboarding_create_user',
              'Created individual user',
              {
                endpoint: '/user/create',
                method: 'POST',
                employeeId: payload['Employee ID'],
                roleName: payload.spec_role,
                designation: payload.Designation,
                hasImage: !!payload.imageData,
                hasSpecData: !!payload.spec_data,
              }
            );
          } catch (logError) {
            console.warn('[UserOnboarding] Failed to log event:', logError);
          }
          
          setSuccess(
            'User created successfully! Check browser console for API response details.'
          );
        }
      } catch (apiError) {
        console.error('❌ API Error:', apiError);
        setError(`API Error: ${apiError.message}`);
        return; // Don't reset form on API error
      }

      // Reset form
      setFormData({
        'Employee ID': '',
        'Full Name': '',
        'Date of Birth': '',
        'Phone Number': '',
        // Email: '',
        Gender: '',
        'Blood Group': '',
        'Area Available': '',
        Address: '',
        'Reports To': '',
        role_name: '',
        'Commercial License': false,
        'International Driving Permit': false,
        '4 Wheeler': false,
        'Heavy Motor': false,
      });
      setImageData(null);
      setImagePreview(null);
      setHasImage(false);
      setImageSuccess('');

      setIsDriver(false);

      console.log('✅ Form reset successfully');
    } catch (err) {
      setError('Error processing form data. Check console for details.');
      console.error('Form processing error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full h-full">
      <div className="max-w-6xl mx-auto h-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 pt-4 pb-2"
        >
          <h1
            className={typography.h1}
            style={{ color: '#000000', marginBottom: '0.5rem' }}
          >
            User Onboarding
          </h1>
          <p className={typography.p} style={{ color: '#6B7280' }}>
            Complete the form below to onboard a new user
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="h-full space-y-8">
          {/* Profile Image Section */}
          <Card.Glassmorphism className="p-6">
            <div className="text-center">
              <h2
                className={typography.h2}
                style={{ color: '#000000', marginBottom: '1.5rem' }}
              >
                Profile Image
              </h2>

              <div className="flex flex-col items-center gap-4">
                {/* Image Preview */}
                <div className="relative">
                  {imagePreview ? (
                    <div className="w-32 h-32 rounded-full border-4 border-gray-300 overflow-hidden">
                      <img
                        src={imagePreview}
                        alt="Profile preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setImageData(null);
                          setHasImage(false);
                          setImageSuccess('');
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Upload Buttons */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                    style={{ color: '#ffffff' }}
                  >
                    <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                    Upload Image
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCameraCapture}
                    className={`flex items-center gap-2 ${
                      isCameraActive
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                    style={{ color: '#ffffff' }}
                  >
                    <Camera className="w-4 h-4" style={{ color: '#ffffff' }} />
                    {isCameraActive ? 'Take Photo' : 'Take Photo'}
                  </Button>
                </div>

                {/* Hidden Canvas for Photo Capture */}
                <canvas
                  ref={canvasRef}
                  className="hidden"
                  width="640"
                  height="480"
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* Camera Preview - Positioned directly below the upload buttons section */}
              {showCameraPreview && (
                <div className="w-full mt-6">
                  <div className="p-4 border border-gray-300 rounded-lg bg-gray-50">
                    <div className="text-center mb-3">
                      <h3 className="text-lg font-medium text-gray-800 mb-2">
                        Camera Preview
                      </h3>
                      {cameraError && (
                        <p className="text-red-600 text-sm mb-2">
                          {cameraError}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-80 h-60 rounded-lg border border-gray-300 bg-black"
                        />

                        <div className="flex justify-center gap-3 mt-3">
                          <Button
                            type="button"
                            onClick={takePhoto}
                            disabled={isCapturing}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 px-4 py-2"
                          >
                            {isCapturing ? 'Capturing...' : 'Capture Photo'}
                          </Button>

                          <Button
                            type="button"
                            onClick={stopCamera}
                            className="bg-gray-600 hover:bg-gray-700 px-4 py-2"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card.Glassmorphism>

          {/* Image Success Message - Displayed right after Profile Image section */}
          {imageSuccess && (
            <div className="self-stretch p-3 bg-green-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-green-200/0 flex flex-col justify-start items-start gap-2.5">
              <div className="justify-start text-green-700 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                {imageSuccess}
              </div>
            </div>
          )}

          {/* General Information Section */}
          <Card.Gradient className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-6 h-6 text-blue-600" />
              <h2 className={typography.h2} style={{ color: '#000000' }}>
                General Information
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="employeeId"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Employee ID *
                </label>
                <Input
                  id="employeeId"
                  type="text"
                  value={formData['Employee ID']}
                  onChange={e =>
                    handleInputChange('Employee ID', e.target.value)
                  }
                  placeholder="Enter Employee ID"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Full Name *
                </label>
                <Input
                  id="fullName"
                  type="text"
                  value={formData['Full Name']}
                  onChange={e => handleInputChange('Full Name', e.target.value)}
                  placeholder="Enter Full Name"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="dateOfBirth"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Date of Birth *
                </label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData['Date of Birth']}
                  onChange={e =>
                    handleInputChange('Date of Birth', e.target.value)
                  }
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="phoneNumber"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Phone Number *
                </label>
                <Input
                  id="phoneNumber"
                  type="tel"
                  value={formData['Phone Number']}
                  onChange={e => {
                    // Only allow numbers, spaces, +, -, and parentheses
                    const value = e.target.value.replace(/[^\d+\-()\s]/g, '');
                    handleInputChange('Phone Number', value);
                  }}
                  placeholder="+91 98765 43210"
                  required
                />
              </div>

              {/* <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData['Email']}
                  onChange={e => handleInputChange('Email', e.target.value)}
                  placeholder="user@example.com"
                />
              </div> */}

              <div>
                <label
                  htmlFor="gender"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Gender
                </label>
                <select
                  id="gender"
                  value={formData['Gender']}
                  onChange={e => handleInputChange('Gender', e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>
                    Select Gender
                  </option>
                  {genders.map(gender => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="bloodGroup"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Blood Group
                </label>
                <select
                  id="bloodGroup"
                  value={formData['Blood Group']}
                  onChange={e =>
                    handleInputChange('Blood Group', e.target.value)
                  }
                  className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" disabled>
                    Select Blood Group
                  </option>
                  {bloodGroups.map(group => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Address
              </label>
              <textarea
                id="address"
                value={formData['Address']}
                onChange={e => handleInputChange('Address', e.target.value)}
                placeholder="Enter complete address"
                rows={3}
                className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </Card.Gradient>

          {/* Work Details Section */}
          <Card.Light className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-6 h-6 text-purple-600" />
              <h2 className={typography.h2} style={{ color: '#000000' }}>
                Work Details
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Role/Designation *
                </label>
                <select
                  id="role"
                  value={formData['role_name']}
                  onChange={e => {
                    const selectedRole = e.target.value;
                    handleInputChange('role_name', selectedRole);
                    // Check if driver role is selected
                    setIsDriver(selectedRole.toLowerCase() === 'driver');
                  }}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={isLoadingData}
                >
                  <option value="" disabled>
                    {isLoadingData ? 'Loading roles...' : 'Select Role'}
                  </option>
                  {Array.isArray(roles) &&
                    roles.map(role => (
                      <option key={role.role_name} value={role.role_name}>
                        {role.display_name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="areaAvailable"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Area Available
                </label>
                <Input
                  id="areaAvailable"
                  type="text"
                  value={formData['Area Available']}
                  onChange={e =>
                    handleInputChange('Area Available', e.target.value)
                  }
                  placeholder="Enter available area (e.g., North Zone, Downtown, etc.)"
                />
              </div>

              <div>
                <label
                  htmlFor="reportsTo"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Reports To
                </label>
                <Input
                  id="reportsTo"
                  type="text"
                  value={formData['Reports To']}
                  onChange={e =>
                    handleInputChange('Reports To', e.target.value)
                  }
                  placeholder="Enter the Employee id this employee is reporting to"
                  required
                />
              </div>
            </div>
          </Card.Light>

          {/* Vehicle License Section - Only for Drivers */}
          {isDriver && (
            <Card.Dark className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Car className="w-6 h-6 text-green-600" />
                <h2 className={typography.h2} style={{ color: '#FFFFFF' }}>
                  Vehicle License
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="commercial"
                    checked={formData['Commercial License']}
                    onChange={e =>
                      handleInputChange('Commercial License', e.target.checked)
                    }
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="commercial"
                    className="text-sm font-medium text-gray-700"
                  >
                    Commercial License
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="international"
                    checked={formData['International Driving Permit']}
                    onChange={e =>
                      handleInputChange(
                        'International Driving Permit',
                        e.target.checked
                      )
                    }
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="international"
                    className="text-sm font-medium text-gray-700"
                  >
                    International Driving Permit
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="fourWheeler"
                    checked={formData['4 Wheeler']}
                    onChange={e =>
                      handleInputChange('4 Wheeler', e.target.checked)
                    }
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="fourWheeler"
                    className="text-sm font-medium text-gray-700"
                  >
                    4 Wheeler License
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="heavyMotor"
                    checked={formData['Heavy Motor']}
                    onChange={e =>
                      handleInputChange('Heavy Motor', e.target.checked)
                    }
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="heavyMotor"
                    className="text-sm font-medium text-gray-700"
                  >
                    Heavy Motor Vehicle License
                  </label>
                </div>
              </div>
            </Card.Dark>
          )}

          {/* Error and Success Messages */}
          {error && (
            <div className="self-stretch p-3 bg-red-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-red-200/0 flex flex-col justify-start items-start gap-2.5">
              <div className="justify-start text-red-700 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                {error}
              </div>
            </div>
          )}

          {success && (
            <div className="self-stretch p-3 bg-green-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-green-200/0 flex flex-col justify-start items-start gap-2.5">
              <div className="justify-start text-green-700 text-lg font-normal font-['Open_Sans'] leading-relaxed">
                {success}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center gap-4 mt-8">
            <PrimaryButton
              type="submit"
              disabled={isLoading}
              className="w-auto px-8"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Save className="w-5 h-5" style={{ color: '#ffffff' }} />
                  Onboarding Employee
                </div>
              )}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserOnboarding;
